/**
 * `<Chip>` — small uppercase tone-label pill.
 *
 * Foundations spec: design/design-system-foundations.md §9.12.
 * 4 tones (strong/moderate/weak/neutral) consume verdict-* and
 * surface-* semantic tokens; ring-1 ring-inset gives the soft outline
 * that distinguishes a chip from a status pill.
 *
 * Used for fit/tenure callouts on RankedCandidateCard (30h scope) and
 * ad-hoc inline tags. Not yet consumed in 30f.
 */
import type { JSX } from "react";

type ChipTone = "strong" | "moderate" | "weak" | "neutral";

interface ChipProps {
  /** Color tone — drives bg / fg / ring tokens. */
  tone: ChipTone;
  /** Visible text. */
  label: string;
  /** Visual size. Default "sm". */
  size?: "sm" | "md";
}

const TONE_CLASS: Record<ChipTone, string> = {
  strong:
    "bg-verdict-strong-bg text-verdict-strong-fg ring-verdict-strong-ring",
  moderate:
    "bg-verdict-moderate-bg text-verdict-moderate-fg ring-verdict-moderate-ring",
  weak: "bg-verdict-weak-bg text-verdict-weak-fg ring-verdict-weak-ring",
  neutral: "bg-surface-subtle text-text-secondary ring-border-default",
};

const SIZE_CLASS = {
  sm: "px-2 py-0.5 text-2xs",
  md: "px-2.5 py-1 text-xs",
} as const;

export function Chip({ tone, label, size = "sm" }: ChipProps): JSX.Element {
  return (
    <span
      data-tone={tone}
      className={`inline-flex items-center rounded-md font-semibold uppercase tracking-wide ring-1 ring-inset ${TONE_CLASS[tone]} ${SIZE_CLASS[size]}`}
    >
      {label}
    </span>
  );
}
